/**
 * FarmlyMarkdown — safe, restricted markdown renderer (Farmly Markdown v1).
 *
 * Allowed: **bold**, _italic_, bullet/ordered lists, paragraphs, line breaks,
 *          [text](https://...) links (https only, noopener/noreferrer).
 * Disallowed: HTML, headings, blockquotes, code, tables, images, embeds.
 *
 * Stored as plain text in DB. Rendered here with react-markdown.
 * SoT: docs/architecture/content-formatting.md
 */

import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import { cn } from "@/lib/utils";
import { unescapeLiteralNewlines } from "@/lib/content/farmlyMarkdown";

interface FarmlyMarkdownProps {
  content: string;
  /** 'body' = standard spacing; 'compact' = tighter for cards/previews */
  variant?: "body" | "compact";
  className?: string;
}

/** Strip any raw HTML tags before passing to renderer (defense-in-depth). */
function stripHtmlTags(text: string): string {
  return text.replace(/<[^>]*>/g, "");
}

/** Only allow https links; everything else rendered as plain text. */
function SafeLink({
  href,
  children,
}: {
  href?: string;
  children?: React.ReactNode;
}) {
  if (href && href.startsWith("https://")) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2 hover:text-primary/80"
      >
        {children}
      </a>
    );
  }
  // Non-https link → render as plain text
  return <>{children}</>;
}

/** Whitelist of allowed elements — everything else is ignored/unwrapped. */
const ALLOWED_COMPONENTS: Components = {
  // Block
  p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
  ul: ({ children }) => (
    <ul className="list-disc pl-5 mb-3 last:mb-0 space-y-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-5 mb-3 last:mb-0 space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li>{children}</li>,

  // Inline
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  a: ({ href, children }) => <SafeLink href={href}>{children}</SafeLink>,
  br: () => <br />,

  // Disallowed — render children only (unwrap)
  h1: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
  h2: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
  h3: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
  h4: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
  h5: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
  h6: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
  blockquote: ({ children }) => <>{children}</>,
  pre: ({ children }) => <>{children}</>,
  code: ({ children }) => <>{children}</>,
  img: () => null,
  table: () => null,
  thead: () => null,
  tbody: () => null,
  tr: () => null,
  th: () => null,
  td: () => null,
  hr: () => null,
};

export function FarmlyMarkdown({
  content,
  variant = "body",
  className,
}: FarmlyMarkdownProps) {
  if (!content) return null;

  const cleaned = stripHtmlTags(unescapeLiteralNewlines(content));

  return (
    <div
      className={cn(
        "text-foreground/75 leading-relaxed",
        variant === "body" && "text-[0.95rem]",
        variant === "compact" && "text-sm",
        className,
      )}
    >
      <ReactMarkdown
        skipHtml
        unwrapDisallowed
        components={ALLOWED_COMPONENTS}
      >
        {cleaned}
      </ReactMarkdown>
    </div>
  );
}
